import AJV from 'ajv';
import HTTPStatusCodes from 'http-status-codes';
import { Either, EitherPatterns } from 'tsmonad';

import { TReqConfigValidated } from '../../../services/JSCService';
import RequestService from '../../../services/RequestService';
import {
  EN_REQUEST_ERRORS,
  IRequesterError,
  IRequesterResponse,
  TRequesterResponse,
} from '../../../services/RequestService/IRequester';
import { captureError, captureRequestErrorResponse } from '../../../services/TrackerService/Sentry';
import { ResponseEither } from '../ResponseEither';
import { noTransform } from '../ResponseEither/transformers/noTransform';
import { ResponseTransformer } from '../ResponseEither/transformers/ResponseTransformer';
import PersonalInfoMasker from './PersonalInfoMasker';

// [todo] write an explainer?
const createJSCError = <P>(error?: AJV.ErrorObject[]): ResponseEither<P> =>
  Either.left({
    data: error,
    errorType: EN_REQUEST_ERRORS.JSON_SCHEMA,
    statusCode: 400,
    success: false,
  });

// [todo] write an explainer
const createActionLeftHandler = <P>(source: string) => (error: AJV.ErrorObject[]): Promise<ResponseEither<P>> => {
  if (error[0] && error[0].dataPath.includes('.resources.')) {
    // 주소 입력 오류는 무시?
    return Promise.resolve(createJSCError(error));
  }
  captureError(error, `JSC error at ${source}`);
  return Promise.resolve(createJSCError(error));
};

const shouldLeaveErrorMsg = <T>(
  response: TRequesterResponse<T>,
  nonErrorCodes: number[],
): response is IRequesterError => !response.success && !nonErrorCodes.includes(response.statusCode);

const shouldLeaveResponse = (): boolean => {
  if (process.browser) {
    return false;
  }
  // TODO: hwboo 성공 응답도 로깅하도록 변경함, 추후 불필요 시 원복
  return !['local'].includes(process.env.RUN_MODE || 'local');

  // if (!response.success) {
  //   return true;
  // }

  /**
   * local 환경과 production 환경을 제외한 나머지 환경을 모든 응답을 남겨
   * 문제해결에 적극적으로 요청/응답을 확인할 수 있도록 한다
   */
  // return !['local', 'production'].includes(process.env.RUN_MODE || 'local');
};

/**
 * 성공 응답 로깅
 * @param response
 */
const sendSuccessActionLog = <T>(response: IRequesterResponse<T>): void => {
  console.log(
    JSON.stringify({
      log_id: 'api:sendSuccessActionLog',
      success: response.success,
      statusCode: response.statusCode,
      url: response.config?.url,
      method: response.config?.method,
      duration: response.duration,
    }),
  );
};

/**
 * 에러 응답 로깅
 *
 * - AxiosRequester.ts 에서 불필요한 값이 제거된 응답이 내려오므로 응답전체를 로깅한다
 * - 로깅에 값 추가가 필요하다면 AxiosRequester.ts 수정을 한다
 * @param response
 */
const snedErrorActionLog = (response: IRequesterError): void => {
  if (response?.config?.data) {
    try {
      response.config.data = JSON.parse(response.config.data);
    } catch (e) {}
  }
  const masker = new PersonalInfoMasker();
  const maskedResponse = masker.maskPersonalInfo(response, [/pass(wd|word)/i, /email/i, /phone/i]);
  maskedResponse.log_id = 'api:snedErrorActionLog';
  console.log(JSON.stringify(maskedResponse));
};

const sendActionLog = <T>(response: TRequesterResponse<T>): void => {
  if (response.success) {
    sendSuccessActionLog(response);
  } else {
    snedErrorActionLog(response);
  }
};

/**
 *
 * @param source 오류 발생 시 센트리 전파에 사용할 오류 발생 위치.
 * @param strat 응답모델의 변경이 필요한 경우 사용하는 함수
 * @param nonErrorCodes status 400 이상 중 알람을 받을 필요 없는 코드 필터링 ex) 401 로그인 세션 종료
 * nonErrorCodes 를 이용해 로깅이 필요한 응답 코드를 갖는다면 sentry 로깅을 한다
 * Axios 를 이용해 요청을 했다면 right 로 처리가 된다 (400 이상의 status 라도 left 로 가지 않는다)
 * Axios 요청 전 requestBuilder 에서 validation 실패가 발생하면 left 로 처리가 된다
 */
const createActionRightHandler = <T>(source: string, strat: ResponseTransformer, nonErrorCodes: number[]) => async (
  request: Promise<TRequesterResponse<T>>,
): Promise<ResponseEither<T>> => {
  const response = await request;
  if (shouldLeaveErrorMsg(response, nonErrorCodes)) {
    captureRequestErrorResponse(response, `Action failed on ${source}`);
  }

  if (shouldLeaveResponse()) {
    sendActionLog(response);
  }
  return strat(response);
};

/**
 * [todo] write jsdoc
 */
export const defaultNonErrorCodes = [
  HTTPStatusCodes.BAD_REQUEST,
  HTTPStatusCodes.UNAUTHORIZED,
  HTTPStatusCodes.FORBIDDEN,
];

// [todo] write an explainer
/**
 * @param source 오류 발생 시 센트리 전파에 사용할 오류 발생 위치.
 * @param strat 응답모델의 변경이 필요한 경우 사용하는 함수
 * @param nonErrorCodes status 400 이상 중 알람을 받을 필요 없는 코드 필터링 ex) 401 로그인 세션 종료
 */
const createActionHandler = <P>(
  source: string,
  strat: ResponseTransformer = noTransform,
  nonErrorCodes: number[] = defaultNonErrorCodes,
): EitherPatterns<AJV.ErrorObject[], Promise<TRequesterResponse<P>>, Promise<ResponseEither<P>>> => {
  return {
    left: createActionLeftHandler(source),
    right: createActionRightHandler(source, strat, nonErrorCodes),
  };
};

/**
 * [todo] write jsdoc
 * @param source 오류 발생 시 센트리 전파에 사용할 오류 발생 위치.
 * @param reqConfig 리퀘스트빌더의 반환값.
 * @param strat 응답을 변경하기 위한 함수, 기본적으로 noTransform 이 사용됨.
 * @param nonErrorCodes status 400 이상 중 알람을 받을 필요 없는 코드 필터링 ex) 401 로그인 세션 종료
 */
export const execAction = <P>(
  source: string,
  reqConfig: TReqConfigValidated,
  strat?: ResponseTransformer,
  nonErrorCodes?: number[],
): Promise<ResponseEither<P>> => {
  return reqConfig
    .map((config) => RequestService.create(config.url).call<P>(config))
    .caseOf(createActionHandler(source, strat, nonErrorCodes));
};
